import {
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Button,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React, { useState } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { FormulaEngine } from '@finley-technologies/finley-types';

import { colors } from 'packages/common-frontend/colors';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { WipConfig } from 'packages/creditAgreements/ui/types';

const createPortfolioCovenantConfigShell = (): CreditAgreement.Config.PortfolioCovenant.Config => ({
  valueType: 'percentage',
  triggerDates: { recurringDateConfigs: [], exclusions: [] },
  globalRecurringDateConfigs: [],
  athenaQueries: [],
  athenaRecurringQueries: [],
  postgresQueries: [],
  intermediateFields: [
    // *** Initial Threshold Placeholder ***
    {
      field: 'LEVEL_1_THRESHOLD',
      config: {
        type: 'formula',
        formula: '"PLACEHOLDER"',
        engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
      },
    },
    // *** Required Fields ***
    {
      field: 'portfolioCovenantReportingDate',
      config: {
        formula: 'TEXT(EOMONTH(triggerDate, -1), "yyyy-mm-dd")',
        type: 'formula',
        engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
      },
    },
    {
      field: 'portfolioCovenantValue',
      config: {
        formula: '"jsNull"',
        type: 'formula',
        engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
      },
    },
    {
      field: 'portfolioCovenantResult',
      config: {
        formula:
          'IF(portfolioCovenantValue < LEVEL_1_THRESHOLD, "PASS", "LEVEL_1_FAIL")',
        type: 'formula',
        engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
      },
    },
  ],
});

export const addPortfolioCovenantConfig = (
  wc: WipConfig,
  fullPropertyKey: string,
): void => {
  const covenant = getIn(wc.formProps.values, fullPropertyKey);
  wc.formProps.setFieldValue(fullPropertyKey, {
    ...covenant,
    _config_: createPortfolioCovenantConfigShell(),
  });
};

type AddPortfolioCovenantModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onClickAdd: ({
    nameInput,
    covenantKeyInput,
  }: {
    nameInput: string;
    covenantKeyInput: string;
  }) => void;
  initialCovenantKey: string;
  initialName: string;
  isAddLoading: boolean;
  mode: 'add' | 'duplicate';
};
export const AddPortfolioCovenantModal = ({
  isOpen,
  onClose,
  onClickAdd,
  initialCovenantKey,
  initialName,
  isAddLoading,
  mode,
}: AddPortfolioCovenantModalProps) => {
  const [name, setName] = useState<string>(initialName);
  const [covenantKey, setCovenantKey] = useState<string>(initialCovenantKey);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {`${commonUtils.toTitleCase(mode)} Portfolio Covenant`}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb="16px" fontWeight={600}>
            Name:
          </Text>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <Text mt="24px" mb="16px" fontWeight={600}>
            Covenant Key:
          </Text>
          <Input
            value={covenantKey}
            onChange={
              (event) => setCovenantKey(_.camelCase(event.target.value)) // only allow camelCase covenantKey
            }
          />
          <Text mt="8px" fontSize="14px" color={colors.black[60]}>
            Auto-formatted in camelCase. This cannot be changed later.
          </Text>
        </ModalBody>

        <ModalFooter>
          <Button
            colorScheme="aurora"
            variant="outline"
            borderWidth="2px"
            mr="32px"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            colorScheme="aurora"
            color="white"
            onClick={() =>
              onClickAdd({ nameInput: name, covenantKeyInput: covenantKey })
            }
            isLoading={isAddLoading}
          >
            {`${commonUtils.toTitleCase(mode)} Portfolio Covenant`}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
